// src/result.ts
function ok(value) {
  return { ok: true, value };
}
function err(error) {
  return { ok: false, error };
}
function isOk(result) {
  return result.ok;
}
function isErr(result) {
  return !result.ok;
}
function map(result, fn) {
  return result.ok ? ok(fn(result.value)) : result;
}
function mapErr(result, fn) {
  return result.ok ? result : err(fn(result.error));
}
function andThen(result, fn) {
  return result.ok ? fn(result.value) : result;
}
function unwrapOr(result, fallback) {
  return result.ok ? result.value : fallback;
}
function all(results) {
  const values = [];
  for (const result of results) {
    if (!result.ok) {
      return result;
    }
    values.push(result.value);
  }
  return ok(values);
}

// src/assert-never.ts
function assertNever(value, message) {
  throw new Error(message ?? `Unexpected value: ${JSON.stringify(value)}`);
}

// src/ids.ts
var ID_PREFIXES = {
  client: "cli",
  user: "usr",
  apiKey: "key",
  useCase: "uc",
  model: "mdl",
  pricingRule: "prc",
  request: "req",
  attempt: "att",
  file: "fil",
  adjustment: "adj",
  period: "per",
  webhookDelivery: "whd",
  alert: "alr",
  batch: "bch",
  charge: "chg",
  platformCharge: "pch",
  capChange: "cch",
  company: "cmp",
  contact: "ctc",
  deal: "dea",
  activity: "act",
  reminder: "rem",
  dealDocument: "ddc",
  dealProposal: "dpr",
  dealInstallment: "din",
  appointmentType: "apt",
  appointment: "apm"
};
var SUFFIX_PATTERN = /^[0-9a-hjkmnp-tv-z]{26}$/;
function createIdCodec(prefix) {
  const parse = (raw) => {
    const marker = `${prefix}_`;
    if (raw.startsWith(marker)) {
      const suffix = raw.slice(marker.length);
      if (SUFFIX_PATTERN.test(suffix)) {
        return ok(raw);
      }
    }
    return err({ type: "id_invalid_format", expectedPrefix: prefix, received: raw });
  };
  return {
    prefix,
    parse,
    is(raw) {
      return parse(raw).ok;
    }
  };
}
var ClientIdCodec = createIdCodec(ID_PREFIXES.client);
var UserIdCodec = createIdCodec(ID_PREFIXES.user);
var ApiKeyIdCodec = createIdCodec(ID_PREFIXES.apiKey);
var UseCaseIdCodec = createIdCodec(ID_PREFIXES.useCase);
var ModelIdCodec = createIdCodec(ID_PREFIXES.model);
var PricingRuleIdCodec = createIdCodec(ID_PREFIXES.pricingRule);
var RequestIdCodec = createIdCodec(ID_PREFIXES.request);
var AttemptIdCodec = createIdCodec(ID_PREFIXES.attempt);
var FileIdCodec = createIdCodec(ID_PREFIXES.file);
var AdjustmentIdCodec = createIdCodec(ID_PREFIXES.adjustment);
var PeriodIdCodec = createIdCodec(ID_PREFIXES.period);
var WebhookDeliveryIdCodec = createIdCodec(
  ID_PREFIXES.webhookDelivery
);
var AlertIdCodec = createIdCodec(ID_PREFIXES.alert);
var BatchIdCodec = createIdCodec(ID_PREFIXES.batch);
var ChargeIdCodec = createIdCodec(ID_PREFIXES.charge);
var PlatformChargeIdCodec = createIdCodec(
  ID_PREFIXES.platformCharge
);
var CapChangeIdCodec = createIdCodec(ID_PREFIXES.capChange);
var CompanyIdCodec = createIdCodec(ID_PREFIXES.company);
var ContactIdCodec = createIdCodec(ID_PREFIXES.contact);
var DealIdCodec = createIdCodec(ID_PREFIXES.deal);
var ActivityIdCodec = createIdCodec(ID_PREFIXES.activity);
var ReminderIdCodec = createIdCodec(ID_PREFIXES.reminder);
var DealDocumentIdCodec = createIdCodec(
  ID_PREFIXES.dealDocument
);
var DealProposalIdCodec = createIdCodec(
  ID_PREFIXES.dealProposal
);
var DealInstallmentIdCodec = createIdCodec(
  ID_PREFIXES.dealInstallment
);
var AppointmentTypeIdCodec = createIdCodec(
  ID_PREFIXES.appointmentType
);
var AppointmentIdCodec = createIdCodec(ID_PREFIXES.appointment);

// src/money.ts
import Decimal from "decimal.js";
var MICRO = new Decimal(1e6);
var ROUND_HALF_UP = Decimal.ROUND_HALF_UP;
function validateMicro(value) {
  if (!Number.isSafeInteger(value)) {
    return err({ type: "micro_not_safe_integer", value });
  }
  return ok(value);
}
var brandEur = (value) => value;
var brandUsd = (value) => value;
var MICRO_EUR_ZERO = brandEur(0);
var MICRO_USD_ZERO = brandUsd(0);
function microEur(value) {
  const validated = validateMicro(value);
  return validated.ok ? ok(brandEur(validated.value)) : validated;
}
function microUsd(value) {
  const validated = validateMicro(value);
  return validated.ok ? ok(brandUsd(validated.value)) : validated;
}
function euroDecimalToMicroEur(euros) {
  const micros = new Decimal(euros).times(MICRO).toDecimalPlaces(0, ROUND_HALF_UP).toNumber();
  return microEur(micros);
}
function usdDecimalToMicroUsd(usd) {
  const micros = new Decimal(usd).times(MICRO).toDecimalPlaces(0, ROUND_HALF_UP).toNumber();
  return microUsd(micros);
}
function microEurToDecimal(amount) {
  return new Decimal(amount).dividedBy(MICRO);
}
function microEurToCents(amount) {
  return microEurToDecimal(amount).times(100).toDecimalPlaces(0, ROUND_HALF_UP).toNumber();
}
function microUsdToDecimal(amount) {
  return new Decimal(amount).dividedBy(MICRO);
}
function formatMicroEur(amount) {
  return microEurToDecimal(amount).toFixed();
}
function formatMicroEurRounded(amount, decimals) {
  return microEurToDecimal(amount).toDecimalPlaces(decimals, ROUND_HALF_UP).toFixed(decimals);
}
function addMicroEur(a, b) {
  return microEur(a + b);
}
function subMicroEur(a, b) {
  return microEur(a - b);
}
function addMicroUsd(a, b) {
  return microUsd(a + b);
}
function compareMicroEur(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
}
function compareMicroUsd(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
}
function convertMicroUsdToMicroEur(amount, usdToEur) {
  const euros = microUsdToDecimal(amount).times(new Decimal(usdToEur));
  return euroDecimalToMicroEur(euros);
}

// src/entities/environment.ts
var ENVIRONMENTS = ["production", "staging"];
function isBilledEnvironment(environment) {
  return environment === "production";
}

// src/entities/client.ts
function validateClientInvariants(input) {
  if (input.safetyCapMicroEur <= 0) {
    return err({ type: "safety_cap_not_positive", value: input.safetyCapMicroEur });
  }
  if (input.clientCapMicroEur !== null && input.clientCapMicroEur <= 0) {
    return err({ type: "client_cap_not_positive", value: input.clientCapMicroEur });
  }
  if (input.defaultUserCapMicroEur !== null) {
    if (input.defaultUserCapMicroEur <= 0) {
      return err({ type: "default_user_cap_not_positive", value: input.defaultUserCapMicroEur });
    }
    if (input.clientCapMicroEur !== null && input.defaultUserCapMicroEur > input.clientCapMicroEur) {
      return err({
        type: "default_user_cap_exceeds_client_cap",
        defaultUserCap: input.defaultUserCapMicroEur,
        clientCap: input.clientCapMicroEur
      });
    }
  }
  const thresholdsValid = input.alertThresholds.every((t) => Number.isInteger(t) && t > 0 && t <= 200);
  if (!thresholdsValid) {
    return err({ type: "invalid_alert_thresholds", value: input.alertThresholds });
  }
  if (!Number.isInteger(input.maxConcurrentRequests) || input.maxConcurrentRequests < 1) {
    return err({ type: "invalid_max_concurrent", value: input.maxConcurrentRequests });
  }
  return ok(void 0);
}
function isClientActive(client) {
  return client.status === "active";
}
function webhookEndpointFor(client, environment) {
  const [url, secretEncrypted] = environment === "staging" ? [client.stagingWebhookUrl, client.stagingWebhookSecretEncrypted] : [client.webhookUrl, client.webhookSecretEncrypted];
  return url === null || secretEncrypted === null ? null : { url, secretEncrypted };
}

// src/entities/api-key.ts
var API_KEY_PREFIXES = {
  service: "maj_svc_",
  admin: "maj_adm_"
};
function apiKeyMarker(kind, environment) {
  return environment === "staging" ? `${API_KEY_PREFIXES[kind]}stg_` : API_KEY_PREFIXES[kind];
}
function isApiKeyActive(key) {
  return key.revokedAt === null;
}
function shouldRefreshLastUsed(lastUsedAt, now) {
  if (lastUsedAt === null) {
    return true;
  }
  return now.getTime() - lastUsedAt.getTime() >= 6e4;
}

// src/entities/end-user.ts
function validateUserCap(userCap, clientCap) {
  if (userCap !== null && clientCap !== null && userCap > clientCap) {
    return err({ type: "user_cap_exceeds_client_cap", userCap, clientCap });
  }
  return ok(void 0);
}
function isEndUserUsable(user) {
  return user.active;
}

// src/entities/cap-change.ts
function capValueChanged(previous, next) {
  return previous !== next;
}

// src/entities/file.ts
var ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp"
];
function isAcceptedMimeType(mimeType) {
  return ACCEPTED_MIME_TYPES.includes(mimeType);
}

// src/entities/webhook-delivery.ts
var WEBHOOK_RETRY_SCHEDULE_MINUTES = [1, 5, 30, 120, 360, 720, 1440];
function nextWebhookDelayMs(attempts) {
  const minutes = WEBHOOK_RETRY_SCHEDULE_MINUTES[attempts - 1];
  return minutes === void 0 ? null : minutes * 6e4;
}

// src/entities/alert.ts
import Decimal2 from "decimal.js";
function exceededThresholds(current, cap, thresholds) {
  if (cap <= 0) {
    return [];
  }
  const ratio = new Decimal2(current).dividedBy(new Decimal2(cap)).times(100);
  return thresholds.filter((t) => ratio.greaterThanOrEqualTo(t)).sort((a, b) => a - b);
}

// src/entities/adjustment.ts
import Decimal3 from "decimal.js";
function validateAdjustment(clientId, percent, requests, alreadyAdjusted) {
  if (!Number.isInteger(percent) || percent < 1 || percent > 100) {
    return err({ type: "invalid_percent" });
  }
  if (requests.length === 0) {
    return err({ type: "no_requests" });
  }
  for (const request of requests) {
    if (request.clientId !== clientId) {
      return err({ type: "wrong_client" });
    }
    if (!request.billable || request.billedMicroEur <= 0) {
      return err({ type: "request_not_billable" });
    }
    if (alreadyAdjusted.has(request.id)) {
      return err({ type: "request_already_adjusted" });
    }
  }
  return ok(void 0);
}
function computeAdjustmentAmount(requests, percent) {
  const factor = new Decimal3(percent).dividedBy(100);
  let total = new Decimal3(0);
  for (const request of requests) {
    total = total.plus(microEurToDecimal(request.billedMicroEur).times(factor));
  }
  return euroDecimalToMicroEur(total);
}

// src/entities/recurring-charge.ts
var MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
function isValidMonth(month) {
  return MONTH_PATTERN.test(month);
}
function isMonthInRange(month, startMonth, endMonth) {
  if (month < startMonth) {
    return false;
  }
  return endMonth === null || month <= endMonth;
}
function smoothFrequencyToMonthly(amount, frequency) {
  switch (frequency) {
    case "monthly":
      return ok(amount);
    case "annual":
      return euroDecimalToMicroEur(microEurToDecimal(amount).dividedBy(12));
    default:
      return assertNever(frequency);
  }
}
function validateRecurringCharge(draft) {
  if (draft.label.trim() === "") {
    return err({ type: "invalid_label" });
  }
  if (draft.billedMicroEur < 0) {
    return err({ type: "invalid_billed_amount" });
  }
  if (draft.costMicroEur < 0) {
    return err({ type: "invalid_cost_amount" });
  }
  if (!isValidMonth(draft.startMonth)) {
    return err({ type: "invalid_month_range" });
  }
  if (draft.endMonth !== null) {
    if (!isValidMonth(draft.endMonth) || draft.endMonth < draft.startMonth) {
      return err({ type: "invalid_month_range" });
    }
  }
  return ok(void 0);
}
function isChargeActiveForMonth(charge, month) {
  return isMonthInRange(month, charge.startMonth, charge.endMonth);
}
function monthlyBilledMicroEur(charge) {
  return smoothFrequencyToMonthly(charge.billedMicroEur, charge.frequency);
}
function monthlyCostMicroEur(charge) {
  return smoothFrequencyToMonthly(charge.costMicroEur, charge.frequency);
}
function recurringChargeLabel(charge) {
  return charge.frequency === "annual" ? `${charge.label} (annuel, liss\xE9 sur 12 mois)` : charge.label;
}

// src/entities/platform-charge.ts
function validatePlatformCharge(draft) {
  if (draft.label.trim() === "") {
    return err({ type: "invalid_label" });
  }
  if (draft.costMicroEur <= 0) {
    return err({ type: "invalid_cost_amount" });
  }
  if (!isValidMonth(draft.startMonth)) {
    return err({ type: "invalid_month_range" });
  }
  if (draft.endMonth !== null) {
    if (!isValidMonth(draft.endMonth) || draft.endMonth < draft.startMonth) {
      return err({ type: "invalid_month_range" });
    }
  }
  return ok(void 0);
}
function isPlatformChargeActiveForMonth(charge, month) {
  return isMonthInRange(month, charge.startMonth, charge.endMonth);
}
function platformChargeMonthlyCostMicroEur(charge) {
  return smoothFrequencyToMonthly(charge.costMicroEur, charge.frequency);
}

// src/catalog/model.ts
var PROVIDERS = ["anthropic", "google", "vonage"];
function defaultCurrencyOf(provider) {
  switch (provider) {
    case "anthropic":
    case "google":
      return "USD";
    case "vonage":
      return "EUR";
    default:
      return assertNever(provider);
  }
}
function modelSupports(model, feature) {
  return model.features.includes(feature);
}
function validateModelFeatures(provider, features) {
  switch (provider) {
    case "vonage":
      return features.length === 1 && features[0] === "sms" ? ok(void 0) : err({ type: "features_incompatible_with_provider" });
    case "anthropic":
    case "google":
      return features.includes("sms") ? err({ type: "features_incompatible_with_provider" }) : ok(void 0);
    default:
      return assertNever(provider);
  }
}
var MODEL_DEFINITION_ERROR_MESSAGES = {
  features_incompatible_with_provider: "Capacit\xE9s incompatibles avec le fournisseur : un service Vonage porte uniquement la capacit\xE9 \xAB sms \xBB, un mod\xE8le d\u2019IA jamais."
};
function modelDefinitionErrorMessage(error) {
  return MODEL_DEFINITION_ERROR_MESSAGES[error.type];
}

// src/catalog/tariff.ts
import Decimal4 from "decimal.js";
var MILLION = new Decimal4(1e6);
function selectTariff(tariffs, mode, at) {
  const target = at.getTime();
  let best;
  for (const tariff of tariffs) {
    if (tariff.mode !== mode || tariff.effectiveFrom.getTime() > target) {
      continue;
    }
    if (best === void 0 || tariff.effectiveFrom.getTime() > best.effectiveFrom.getTime()) {
      best = tariff;
    }
  }
  return best;
}
function attemptCostInTariffCurrency(tariff, usage) {
  const tokenCost = new Decimal4(usage.inputTokens).times(tariff.inputPerMTok).plus(new Decimal4(usage.outputTokens).times(tariff.outputPerMTok)).plus(new Decimal4(usage.cacheWriteTokens).times(tariff.cacheWritePerMTok)).plus(new Decimal4(usage.cacheReadTokens).times(tariff.cacheReadPerMTok)).dividedBy(MILLION);
  const imageCost = new Decimal4(usage.outputImages).times(tariff.perOutputImage);
  const messageCost = new Decimal4(usage.messages).times(tariff.perMessage);
  return tokenCost.plus(imageCost).plus(messageCost);
}
function conversionRate(conversion) {
  switch (conversion.currency) {
    case "USD":
      return conversion.usdToEur;
    case "EUR":
      return null;
    default:
      return assertNever(conversion);
  }
}
function tariffCostInEur(cost, conversion) {
  switch (conversion.currency) {
    case "USD":
      return cost.times(new Decimal4(conversion.usdToEur));
    case "EUR":
      return cost;
    default:
      return assertNever(conversion);
  }
}
function attemptCostMicroUsd(tariff, usage) {
  switch (tariff.currency) {
    case "USD":
      return usdDecimalToMicroUsd(attemptCostInTariffCurrency(tariff, usage));
    case "EUR":
      return ok(null);
    default:
      return assertNever(tariff.currency);
  }
}
function attemptCostMicroEur(tariff, usage, conversion) {
  return euroDecimalToMicroEur(tariffCostInEur(attemptCostInTariffCurrency(tariff, usage), conversion));
}

// src/catalog/exchange-rate.ts
function selectExchangeRate(rates, date) {
  let best;
  for (const rate of rates) {
    if (rate.date <= date && (best === void 0 || rate.date > best.date)) {
      best = rate;
    }
  }
  if (best === void 0) {
    return err({ type: "no_exchange_rate", date });
  }
  return ok(best);
}
function selectConversion(currency, rates, date) {
  switch (currency) {
    case "USD": {
      const rate = selectExchangeRate(rates, date);
      return rate.ok ? ok({ currency: "USD", usdToEur: rate.value.usdToEur }) : rate;
    }
    case "EUR":
      return ok({ currency: "EUR" });
    default:
      return assertNever(currency);
  }
}

// src/messaging/calling-codes.ts
var ASSIGNED_CODE_ROWS = [
  // Zone 1: North American Numbering Plan (United States, Canada, part of the Caribbean).
  "1",
  // Zone 2: Africa and a few other territories (262 Réunion and Mayotte, 297 Aruba, 299 Greenland).
  "20 27",
  "211 212 213 216 218",
  "220 221 222 223 224 225 226 227 228 229",
  "230 231 232 233 234 235 236 237 238 239",
  "240 241 242 243 244 245 246 247 248 249",
  "250 251 252 253 254 255 256 257 258",
  "260 261 262 263 264 265 266 267 268 269",
  "290 291 297 298 299",
  // Zones 3 and 4: Europe.
  "30 31 32 33 34 36 39",
  "350 351 352 353 354 355 356 357 358 359",
  "370 371 372 373 374 375 376 377 378 379",
  "380 381 382 383 385 386 387 389",
  "40 41 43 44 45 46 47 48 49",
  "420 421 423",
  // Zone 5: Central and South America (590 Guadeloupe, 594 French Guiana, 596 Martinique).
  "51 52 53 54 55 56 57 58",
  "500 501 502 503 504 505 506 507 508 509",
  "590 591 592 593 594 595 596 597 598 599",
  // Zone 6: Southeast Asia and Oceania (687 New Caledonia, 689 French Polynesia).
  "60 61 62 63 64 65 66",
  "670 672 673 674 675 676 677 678 679",
  "680 681 682 683 685 686 687 688 689",
  "690 691 692",
  // Zone 7: Russia and Kazakhstan.
  "7",
  // Zone 8: East Asia (800, 808, 870, 878, 881 to 883 and 888 are global services).
  "81 82 84 86",
  "850 852 853 855 856",
  "880 886",
  // Zone 9: Middle East, South and Central Asia (979 and 991 are global services).
  "90 91 92 93 94 95 98",
  "960 961 962 963 964 965 966 967 968",
  "970 971 972 973 974 975 976 977",
  "992 993 994 995 996 998"
];
var COUNTRY_CALLING_CODES = new Set(
  ASSIGNED_CODE_ROWS.flatMap((row) => row.split(" "))
);

// src/messaging/sms.ts
var SMS_MAX_SEGMENTS = 6;
var GSM7_BASIC = `@\xA3$\xA5\xE8\xE9\xF9\xEC\xF2\xC7
\xD8\xF8\r\xC5\xE5\u0394_\u03A6\u0393\u039B\u03A9\u03A0\u03A8\u03A3\u0398\u039E\xC6\xE6\xDF\xC9 !"#\xA4%&'()*+,-./0123456789:;<=>?\xA1ABCDEFGHIJKLMNOPQRSTUVWXYZ\xC4\xD6\xD1\xDC\xA7\xBFabcdefghijklmnopqrstuvwxyz\xE4\xF6\xF1\xFC\xE0`;
var GSM7_EXTENSION = "\f^{}\\[~]|\u20AC";
var GSM7_BASIC_SET = new Set(GSM7_BASIC);
var GSM7_EXTENSION_SET = new Set(GSM7_EXTENSION);
var CAPACITY = {
  gsm7: { single: 160, perPart: 153 },
  ucs2: { single: 70, perPart: 67 }
};
var LOOK_ALIKE_REPLACEMENTS = [
  ["'", "\u2018\u2019\u201A\u201B\u2032\u02BC\xB4`"],
  ['"', "\u201C\u201D\u201E\u201F\xAB\xBB\u2033"],
  ["-", "\u2010\u2011\u2012\u2013\u2014\u2015\u2212"],
  [" ", "\xA0\u202F\u2007\u2009\u200A\u2002\u2003\u2004\u2005\u2006\u2008\u205F\u3000"],
  ["...", "\u2026"],
  ["<", "\u2039"],
  [">", "\u203A"],
  ["", "\u200B\u200C\u200D\u2060\uFEFF\xAD"]
];
var LOOK_ALIKES = new Map(
  LOOK_ALIKE_REPLACEMENTS.flatMap(
    ([replacement, characters]) => Array.from(characters, (character) => [character, replacement])
  )
);
var MAX_REPORTED_CHARACTERS = 10;
function measureSms(text) {
  const normalized = text.normalize("NFC").replace(/\r\n/g, "\n");
  const converted = replaceLookAlikes(normalized);
  const nonGsm = nonGsmCharacters(converted);
  if (nonGsm.length === 0) {
    return { text: converted, encoding: "gsm7", ...pack(converted, "gsm7"), nonGsmCharacters: [] };
  }
  return { text: normalized, encoding: "ucs2", ...pack(normalized, "ucs2"), nonGsmCharacters: nonGsm };
}
function replaceLookAlikes(text) {
  let replaced = "";
  for (const character of text) {
    replaced += LOOK_ALIKES.get(character) ?? character;
  }
  return replaced;
}
function isGsm7Character(character) {
  return GSM7_BASIC_SET.has(character) || GSM7_EXTENSION_SET.has(character);
}
function nonGsmCharacters(text) {
  const found = [];
  for (const character of text) {
    if (!isGsm7Character(character) && !found.includes(character)) {
      found.push(character);
      if (found.length === MAX_REPORTED_CHARACTERS) {
        break;
      }
    }
  }
  return found;
}
function characterSize(character, encoding) {
  switch (encoding) {
    case "gsm7":
      return GSM7_EXTENSION_SET.has(character) ? 2 : 1;
    case "ucs2":
      return character.length;
    default:
      return assertNever(encoding);
  }
}
function pack(text, encoding) {
  const { single, perPart } = CAPACITY[encoding];
  const sizes = Array.from(text, (character) => characterSize(character, encoding));
  const length = sizes.reduce((sum2, size) => sum2 + size, 0);
  if (length <= single) {
    return { length, segments: 1 };
  }
  let segments = 1;
  let used = 0;
  for (const size of sizes) {
    if (used + size > perPart) {
      segments += 1;
      used = 0;
    }
    used += size;
  }
  return { length, segments };
}
var SMS_DESTINATION_NOT_ALLOWED_MESSAGE = "Destination non autoris\xE9e pour ce cas d\u2019usage : l\u2019indicatif du num\xE9ro n\u2019est pas dans la liste autoris\xE9e.";
var SMS_EMPTY_MESSAGE = "SMS vide : le texte ne contient aucun caract\xE8re visible.";
var NOTHING_VISIBLE = /\s|\u200B|\u200C|\u200D|\u2060|\u00AD/g;
function isBlankSms(text) {
  return text.replace(NOTHING_VISIBLE, "") === "";
}
function smsTooLongMessage(segments) {
  return `SMS trop long : ${segments} SMS n\xE9cessaires, ${SMS_MAX_SEGMENTS} au maximum.`;
}
var GENERIC_SMS_SENDERS = [
  "INFO",
  "INFOS",
  "SMS",
  "OTP",
  "CODE",
  "VERIFY",
  "VERIF",
  "NOTICE",
  "NOTIF",
  "ALERT",
  "ALERTE",
  "AMENDES",
  "MESSAGE"
];
var SENDER_PATTERN = /^[A-Za-z0-9]{3,11}$/;
var SENDER_LETTER = /[A-Za-z]/;
function parseSmsSender(raw) {
  const sender = raw.trim();
  if (!SENDER_PATTERN.test(sender) || !SENDER_LETTER.test(sender)) {
    return err({ type: "sender_invalid_format" });
  }
  if (GENERIC_SMS_SENDERS.includes(sender.toUpperCase())) {
    return err({ type: "sender_generic", sender });
  }
  return ok(sender);
}
var CALLING_CODE_PATTERN = /^[1-9]\d{0,3}$/;
var MAX_COUNTRY_CODE_LENGTH = 3;
var MAX_CALLING_CODES = 20;
function isAssignedCallingCode(code) {
  if (!CALLING_CODE_PATTERN.test(code)) {
    return false;
  }
  for (let length = 1; length <= Math.min(MAX_COUNTRY_CODE_LENGTH, code.length); length += 1) {
    if (COUNTRY_CALLING_CODES.has(code.slice(0, length))) {
      return true;
    }
  }
  return false;
}
function parseCallingCodes(raw) {
  const codes = raw.map((code) => code.trim());
  if (codes.length === 0 || codes.length > MAX_CALLING_CODES || !codes.every((code) => isAssignedCallingCode(code)) || new Set(codes).size !== codes.length) {
    return err({ type: "calling_codes_invalid" });
  }
  return ok(codes);
}
function isDestinationAllowed(to, allowedCallingCodes) {
  return allowedCallingCodes.some((code) => to.startsWith(`+${code}`));
}
function validateSmsSettings(raw) {
  const sender = parseSmsSender(raw.sender);
  if (!sender.ok) {
    return sender;
  }
  const allowedCallingCodes = parseCallingCodes(raw.allowedCallingCodes);
  if (!allowedCallingCodes.ok) {
    return allowedCallingCodes;
  }
  return ok({ sender: sender.value, allowedCallingCodes: allowedCallingCodes.value });
}
function smsSettingsErrorMessage(error) {
  switch (error.type) {
    case "sender_invalid_format":
      return "Exp\xE9diteur invalide : 3 \xE0 11 lettres ou chiffres, sans espace, accent ni ponctuation, avec au moins une lettre.";
    case "sender_generic":
      return `\xAB ${error.sender.toUpperCase()} \xBB est un mot g\xE9n\xE9rique refus\xE9 par les op\xE9rateurs fran\xE7ais (charte af2m) : utilisez le nom du client ou d\u2019une de ses marques.`;
    case "calling_codes_invalid":
      return "Indicatifs pays invalides : 1 \xE0 20 indicatifs pays attribu\xE9s et distincts, en chiffres (ex. 33, 262, 336) ; \xAB 2 \xBB ou \xAB 26 \xBB ne sont pas des indicatifs pays.";
    default:
      return assertNever(error);
  }
}

// src/catalog/use-case.ts
var INVOICE_NAME_MAX = 40;
var INVOICE_DESCRIPTION_MAX = 300;
function invoiceLineTitle(useCase) {
  return useCase.invoiceName ?? useCase.key.slice(0, INVOICE_NAME_MAX);
}
function useCaseCategory(kind) {
  switch (kind) {
    case "text":
    case "image":
      return "ai";
    case "sms":
      return "communication";
    default:
      return assertNever(kind);
  }
}
function kindSupportsBatch(kind) {
  switch (kind) {
    case "text":
      return true;
    case "image":
    case "sms":
      return false;
    default:
      return assertNever(kind);
  }
}
function kindSupportsFiles(kind) {
  switch (kind) {
    case "text":
      return true;
    case "image":
    case "sms":
      return false;
    default:
      return assertNever(kind);
  }
}
function validateUseCaseOwnership(input) {
  if (input.ownerClientId === null) {
    return err({ type: "missing_owner" });
  }
  return ok(void 0);
}
function validateUseCaseConfig(input) {
  switch (input.kind) {
    case "text":
    case "image":
      if (input.sms !== null) {
        return err({ type: "sms_settings_not_allowed" });
      }
      if (input.modelFeatures.includes("sms")) {
        return err({ type: "model_not_suitable" });
      }
      return ok(null);
    case "sms":
      if (!input.modelFeatures.includes("sms")) {
        return err({ type: "model_not_suitable" });
      }
      if (input.executionMode === "batch" && !kindSupportsBatch(input.kind)) {
        return err({ type: "batch_not_supported" });
      }
      if (input.acceptsFiles && !kindSupportsFiles(input.kind)) {
        return err({ type: "files_not_supported" });
      }
      if (input.sms === null) {
        return err({ type: "sms_settings_required" });
      }
      return validateSmsSettings(input.sms);
    default:
      return assertNever(input.kind);
  }
}
function useCaseConfigErrorMessage(error) {
  switch (error.type) {
    case "model_not_suitable":
      return "Le mod\xE8le choisi ne convient pas \xE0 ce type de cas d\u2019usage (un cas d\u2019usage SMS exige un service SMS du catalogue, un cas d\u2019usage IA un mod\xE8le d\u2019IA).";
    case "batch_not_supported":
      return "Le mode lot n\u2019est pas disponible pour ce type de cas d\u2019usage.";
    case "files_not_supported":
      return "Ce type de cas d\u2019usage n\u2019accepte pas de fichiers.";
    case "sms_settings_required":
      return "Un cas d\u2019usage SMS exige un exp\xE9diteur et des indicatifs autoris\xE9s.";
    case "sms_settings_not_allowed":
      return "Les r\xE9glages SMS ne s\u2019appliquent qu\u2019\xE0 un cas d\u2019usage SMS.";
    case "sender_invalid_format":
    case "sender_generic":
    case "calling_codes_invalid":
      return smsSettingsErrorMessage(error);
    default:
      return assertNever(error);
  }
}
function tariffModeFor(executionMode) {
  return executionMode === "batch" ? "batch" : "standard";
}

// src/pricing/pricing-rule.ts
import Decimal5 from "decimal.js";
var MILLION2 = new Decimal5(1e6);
var RESERVATION_MARGIN = new Decimal5("1.2");
function resolvePricingRule(rules, useCaseId, clientId, at) {
  const inEffect = (r) => r.useCaseId === useCaseId && r.effectiveFrom.getTime() <= at.getTime();
  const latest = (candidates) => {
    let best;
    for (const r of candidates) {
      if (best === void 0 || r.effectiveFrom.getTime() > best.effectiveFrom.getTime()) {
        best = r;
      }
    }
    return best;
  };
  const clientRule = latest(rules.filter((r) => inEffect(r) && r.clientId === clientId));
  if (clientRule !== void 0) {
    return clientRule;
  }
  return latest(rules.filter((r) => inEffect(r) && r.clientId === null));
}
function resolveDefaultPricingRule(rules, useCaseId, at) {
  let best;
  for (const r of rules) {
    if (r.useCaseId !== useCaseId || r.clientId !== null) continue;
    if (r.effectiveFrom.getTime() > at.getTime()) continue;
    if (best === void 0 || r.effectiveFrom.getTime() > best.effectiveFrom.getTime()) {
      best = r;
    }
  }
  return best;
}
function computeBilledAmount(body, basis) {
  switch (body.type) {
    case "multiplier": {
      const eur = microEurToDecimal(basis.finalAttemptCostMicroEur).times(
        new Decimal5(body.coefficient)
      );
      return euroDecimalToMicroEur(eur);
    }
    case "fixed_per_request":
      return euroDecimalToMicroEur(microEurToDecimal(body.amountMicroEur));
    case "per_unit": {
      const eur = microEurToDecimal(body.amountMicroEur).times(new Decimal5(basis.units));
      return euroDecimalToMicroEur(eur);
    }
  }
}
function estimateProviderCostMicroEur(inputs) {
  return euroDecimalToMicroEur(providerCostEur(inputs));
}
function estimateReservationMicroEur(body, inputs) {
  switch (body.type) {
    case "multiplier": {
      const estimate = providerCostEur(inputs).times(new Decimal5(body.coefficient)).times(RESERVATION_MARGIN);
      return euroDecimalToMicroEur(estimate);
    }
    case "fixed_per_request":
      return euroDecimalToMicroEur(microEurToDecimal(body.amountMicroEur));
    case "per_unit": {
      const eur = microEurToDecimal(body.amountMicroEur).times(new Decimal5(inputs.units));
      return euroDecimalToMicroEur(eur);
    }
  }
}
function providerCostEur(inputs) {
  const cost = new Decimal5(inputs.estimatedInputTokens).times(inputs.inputPerMTok).plus(new Decimal5(inputs.maxOutputTokens).times(inputs.outputPerMTok)).dividedBy(MILLION2).plus(new Decimal5(inputs.estimatedMessages).times(inputs.perMessage));
  return tariffCostInEur(cost, inputs.conversion);
}
function validatePricingRuleForKind(kind, body) {
  switch (body.type) {
    case "multiplier":
    case "fixed_per_request":
      return ok(void 0);
    case "per_unit":
      return body.unit === "sms" === (kind === "sms") ? ok(void 0) : err({ type: "unit_incompatible" });
    default:
      return assertNever(body);
  }
}
var PRICING_RULE_KIND_ERROR_MESSAGES = {
  unit_incompatible: "Unit\xE9 de prix incompatible avec le type du cas d\u2019usage (un cas d\u2019usage SMS se facture au SMS, l\u2019unit\xE9 SMS est r\xE9serv\xE9e aux cas d\u2019usage SMS)."
};
function pricingRuleKindErrorMessage(error) {
  return PRICING_RULE_KIND_ERROR_MESSAGES[error.type];
}

// src/pricing/invoice-composition.ts
import Decimal6 from "decimal.js";
var SUBSCRIPTIONS_SECTION_TITLE = "Abonnements";
var USAGE_SECTION_TITLE = "Utilisation";
var ADJUSTMENTS_SECTION_TITLE = "Gestes commerciaux";
function composeInvoiceDocument(usage, charges, adjustments) {
  const sections = [];
  pushSection(sections, "subscriptions", SUBSCRIPTIONS_SECTION_TITLE, charges.map(chargeItem));
  const lines = [...usage].sort((a, b) => a.title.localeCompare(b.title, "fr"));
  pushSection(sections, "usage", USAGE_SECTION_TITLE, lines.map(usageItem));
  pushSection(sections, "adjustments", ADJUSTMENTS_SECTION_TITLE, adjustments.map(adjustmentItem));
  const totalCents = sections.flatMap((s) => s.items).reduce((sum2, item) => sum2 + item.amountCents, 0);
  return { sections, totalCents };
}
function pushSection(sections, kind, title, items) {
  const kept = items.filter((item) => item.amountCents !== 0);
  if (kept.length > 0) {
    sections.push({ kind, title, items: kept });
  }
}
function chargeItem(charge) {
  const cents = microEurToCents(charge.monthlyMicroEur);
  const fitted = fitTitle(charge.label);
  const notes = [
    fitted === charge.label ? null : charge.label,
    charge.frequency === "annual" ? "Montant annuel liss\xE9 sur 12 mois." : null
  ];
  return {
    title: fitted,
    description: joinNotes(notes),
    quantity: "1",
    // One month of the charge (an annual one is smoothed). `month` is one of the
    // few keywords Qonto turns into an official unit code in the e-invoice (MON).
    unit: "month",
    unitPrice: centsToEuros(cents),
    amountCents: cents
  };
}
function usageItem(group) {
  const cents = microEurToCents(group.billedMicroEur);
  const title = fitTitle(group.title);
  if (group.ruleBody === null) {
    return lumpSum(group, title, cents, requestCountLabel(group.requestCount));
  }
  switch (group.ruleBody.type) {
    case "per_unit": {
      const price = group.ruleBody.amountMicroEur;
      const unit = unitName(group.ruleBody.unit, group.units);
      if (isExactProduct(group.units, price, group.billedMicroEur)) {
        return isCentPrice(price) ? unitPriceItem(group, title, String(group.units), unit, price, cents) : lumpSum(group, title, cents, `${group.units} ${unit} \xE0 ${unitPriceLabel(price)} \u20AC`);
      }
      return lumpSum(group, title, cents, `${group.units} ${unit}`);
    }
    case "fixed_per_request": {
      const price = group.ruleBody.amountMicroEur;
      const unit = pluralize(group.requestCount, "requ\xEAte", "requ\xEAtes");
      if (isExactProduct(group.requestCount, price, group.billedMicroEur)) {
        return isCentPrice(price) ? unitPriceItem(group, title, String(group.requestCount), unit, price, cents) : lumpSum(group, title, cents, `${group.requestCount} ${unit} \xE0 ${unitPriceLabel(price)} \u20AC`);
      }
      return lumpSum(group, title, cents, `${group.requestCount} ${unit}`);
    }
    case "multiplier":
      return lumpSum(group, title, cents, requestCountLabel(group.requestCount));
    default:
      return assertNever(group.ruleBody);
  }
}
function unitPriceItem(group, title, quantity, unit, price, cents) {
  return {
    title,
    description: group.description,
    quantity,
    unit,
    unitPrice: centsToEuros(microEurToCents(price)),
    amountCents: cents
  };
}
function lumpSum(group, title, cents, detail) {
  return {
    title,
    description: joinNotes([group.description, detail]),
    quantity: "1",
    unit: "forfait",
    unitPrice: centsToEuros(cents),
    amountCents: cents
  };
}
function adjustmentItem(adjustment) {
  const cents = microEurToCents(adjustment.amountMicroEur);
  return {
    title: "Geste commercial",
    description: adjustment.reason.trim() === "" ? null : adjustment.reason.trim(),
    quantity: "1",
    unit: null,
    unitPrice: centsToEuros(-cents),
    amountCents: -cents
  };
}
function isCentPrice(price) {
  return price % 1e4 === 0;
}
function isExactProduct(count, price, billed) {
  return count > 0 && count * price === billed;
}
function unitName(unit, count) {
  switch (unit) {
    case "page":
      return pluralize(count, "page", "pages");
    case "image":
      return pluralize(count, "image", "images");
    case "sms":
      return "SMS";
    default:
      return assertNever(unit);
  }
}
function requestCountLabel(count) {
  return `${count} ${pluralize(count, "requ\xEAte", "requ\xEAtes")}`;
}
function pluralize(count, singular, plural) {
  return count <= 1 ? singular : plural;
}
function fitTitle(title) {
  const trimmed = title.trim();
  return trimmed.length <= INVOICE_NAME_MAX ? trimmed : `${trimmed.slice(0, INVOICE_NAME_MAX - 1)}\u2026`;
}
function joinNotes(notes) {
  const kept = notes.filter((n) => n !== null && n.trim() !== "");
  return kept.length === 0 ? null : kept.join("\n");
}
function centsToEuros(cents) {
  return new Decimal6(cents).dividedBy(100).toFixed(2);
}
function unitPriceLabel(micro) {
  const euros = microEurToDecimal(micro).toDecimalPlaces(6, Decimal6.ROUND_HALF_UP);
  return euros.toFixed(Math.max(2, euros.decimalPlaces())).replace(".", ",");
}

// src/lifecycle/status.ts
var TERMINAL = /* @__PURE__ */ new Set([
  "rejected",
  "succeeded",
  "failed",
  "cancelled",
  "expired"
]);
function isTerminalStatus(status) {
  return TERMINAL.has(status);
}
var TRANSITIONS = {
  paused: ["queued", "cancelled", "expired"],
  queued: ["running", "batched", "paused", "cancelled"],
  running: ["queued", "succeeded", "failed"],
  batched: ["succeeded", "failed", "queued"],
  rejected: [],
  succeeded: [],
  failed: [],
  cancelled: [],
  expired: []
};
function canTransition(from, to) {
  return TRANSITIONS[from].includes(to);
}
function transition(from, to) {
  if (!canTransition(from, to)) {
    return err({ type: "invalid_transition", from, to });
  }
  return ok(to);
}

// src/lifecycle/outcome.ts
function isBillable(outcome) {
  switch (outcome) {
    case "processing_error":
    case "content_refused":
    case "output_truncated":
    case "invalid_output":
    case "succeeded":
      return true;
    case "rejected_invalid_input":
    case "rejected_use_case_unavailable":
    case "rejected_user_unknown":
    case "rejected_user_inactive":
    case "rejected_cap_reached":
    case "provider_unavailable":
    case "provider_rejected":
    case "provider_unconfirmed":
    case "cancelled":
    case "expired":
    case "internal_error":
      return false;
    default:
      return assertNever(outcome);
  }
}
function statusForOutcome(outcome) {
  switch (outcome) {
    case "rejected_invalid_input":
    case "rejected_use_case_unavailable":
    case "rejected_user_unknown":
    case "rejected_user_inactive":
    case "rejected_cap_reached":
      return "rejected";
    case "succeeded":
      return "succeeded";
    case "cancelled":
      return "cancelled";
    case "expired":
      return "expired";
    case "provider_unavailable":
    case "provider_rejected":
    case "provider_unconfirmed":
    case "processing_error":
    case "content_refused":
    case "output_truncated":
    case "invalid_output":
    case "internal_error":
      return "failed";
    default:
      return assertNever(outcome);
  }
}

// src/lifecycle/retry.ts
var MAX_PROVIDER_UNAVAILABLE_ATTEMPTS = 4;
var PROVIDER_BACKOFF_MS = [5e3, 3e4, 12e4];
function decideRetry(ctx) {
  if (ctx.outcome === "provider_unavailable") {
    if (ctx.attemptNumber >= MAX_PROVIDER_UNAVAILABLE_ATTEMPTS) {
      return { type: "finalize" };
    }
    const base = PROVIDER_BACKOFF_MS[ctx.attemptNumber - 1] ?? PROVIDER_BACKOFF_MS.at(-1) ?? 5e3;
    const delayMs = ctx.retryAfterMs !== void 0 ? Math.max(ctx.retryAfterMs, base) : base;
    return { type: "retry", delayMs };
  }
  return { type: "finalize" };
}

// src/lifecycle/caps.ts
function checkCaps(input) {
  if (input.platformCap !== null && input.projectedPlatformCost > input.platformCap) {
    return { type: "pause", scope: "platform" };
  }
  if (input.safetyCap !== null && input.projectedClientBilled > input.safetyCap) {
    return { type: "pause", scope: "safety" };
  }
  if (input.clientCap !== null && input.projectedClientBilled > input.clientCap) {
    return { type: "reject", scope: "client" };
  }
  if (input.userCap !== null && input.projectedUserBilled > input.userCap) {
    return { type: "reject", scope: "user" };
  }
  return { type: "allow" };
}

// src/lifecycle/period.ts
var PARIS_MONTH = new Intl.DateTimeFormat("fr-FR", {
  timeZone: "Europe/Paris",
  year: "numeric",
  month: "2-digit"
});
var PARIS_DATE = new Intl.DateTimeFormat("fr-FR", {
  timeZone: "Europe/Paris",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});
function billingMonth(instant) {
  const parts = PARIS_MONTH.formatToParts(instant);
  const year = parts.find((p) => p.type === "year")?.value ?? "1970";
  const month = parts.find((p) => p.type === "month")?.value ?? "01";
  return `${year}-${month}`;
}
function billingDate(instant) {
  const parts = PARIS_DATE.formatToParts(instant);
  const year = parts.find((p) => p.type === "year")?.value ?? "1970";
  const month = parts.find((p) => p.type === "month")?.value ?? "01";
  const day = parts.find((p) => p.type === "day")?.value ?? "01";
  return `${year}-${month}-${day}`;
}
function monthBounds(month) {
  const [yearStr, monthStr] = month.split("-");
  const lastDay = new Date(Date.UTC(Number(yearStr), Number(monthStr), 0)).getUTCDate();
  return { first: `${month}-01`, last: `${month}-${String(lastDay).padStart(2, "0")}` };
}
var FRENCH_MONTHS = [
  "janvier",
  "f\xE9vrier",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "ao\xFBt",
  "septembre",
  "octobre",
  "novembre",
  "d\xE9cembre"
];
function frenchMonthLabel(month) {
  const [yearStr, monthStr] = month.split("-");
  const name = FRENCH_MONTHS[Number(monthStr) - 1];
  return name === void 0 ? month : `${name} ${yearStr ?? ""}`;
}
var PARIS_DATE_TIME = new Intl.DateTimeFormat("fr-FR", {
  timeZone: "Europe/Paris",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23"
});
function frenchDateTime(instant) {
  const parts = PARIS_DATE_TIME.formatToParts(instant);
  const part2 = (type) => parts.find((p) => p.type === type)?.value ?? "00";
  return `${part2("day")}/${part2("month")}/${part2("year")} ${part2("hour")}:${part2("minute")}:${part2("second")}`;
}
function addDaysToDate(date, days) {
  const [yearStr, monthStr, dayStr] = date.split("-");
  const shifted = new Date(Date.UTC(Number(yearStr), Number(monthStr) - 1, Number(dayStr) + days));
  return shifted.toISOString().slice(0, 10);
}
function previousMonth(month) {
  const [yearStr, monthStr] = month.split("-");
  const year = Number(yearStr);
  const monthIndex = Number(monthStr);
  if (!Number.isInteger(year) || !Number.isInteger(monthIndex)) {
    return month;
  }
  if (monthIndex <= 1) {
    return `${year - 1}-12`;
  }
  return `${year}-${String(monthIndex - 1).padStart(2, "0")}`;
}

// src/crm/paris-time.ts
var PARIS_PARTS = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Paris",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23"
});
var DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
var TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
function part(parts, type) {
  return Number(parts.find((p) => p.type === type)?.value ?? "0");
}
function parisOffsetMs(instant) {
  const parts = PARIS_PARTS.formatToParts(instant);
  const asUtc = Date.UTC(
    part(parts, "year"),
    part(parts, "month") - 1,
    part(parts, "day"),
    part(parts, "hour"),
    part(parts, "minute"),
    part(parts, "second")
  );
  return asUtc - Math.floor(instant.getTime() / 1e3) * 1e3;
}
function isValidCalendarDate(date) {
  const match = DATE_PATTERN.exec(date);
  if (match === null) {
    return false;
  }
  const [, y, m, d] = match;
  const probe = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  return probe.toISOString().slice(0, 10) === date;
}
function parisInstant(date, time) {
  if (!isValidCalendarDate(date)) {
    return err({ type: "invalid_date" });
  }
  const t = TIME_PATTERN.exec(time);
  if (t === null) {
    return err({ type: "invalid_time" });
  }
  const [y, m, d] = date.split("-").map(Number);
  const wall = Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1, Number(t[1]), Number(t[2]));
  const guess = wall - parisOffsetMs(new Date(wall));
  return ok(new Date(wall - parisOffsetMs(new Date(guess))));
}
function parisDate(instant) {
  return billingDate(instant);
}
function parisTime(instant) {
  const parts = PARIS_PARTS.formatToParts(instant);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(part(parts, "hour"))}:${pad(part(parts, "minute"))}`;
}
function addMonthsToDate(date, months) {
  const [y, m, d] = date.split("-").map(Number);
  const target = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)
  ).getUTCDate();
  target.setUTCDate(Math.min(d ?? 1, lastDay));
  return target.toISOString().slice(0, 10);
}
function parisInstantAfterDays(from, days, time) {
  const result = parisInstant(addDaysToDate(parisDate(from), days), time);
  if (!result.ok) {
    throw new Error(`Invalid Paris time "${time}"`);
  }
  return result.value;
}
function parisDaysBetween(from, to) {
  const a = Date.parse(`${parisDate(from)}T00:00:00Z`);
  const b = Date.parse(`${parisDate(to)}T00:00:00Z`);
  return Math.round((b - a) / 864e5);
}

// src/crm/company.ts
var COMPANY_ORIGINS = [
  "network",
  "word_of_mouth",
  "existing_client",
  "linkedin",
  "website",
  "other"
];
var SIREN_PATTERN = /^\d{9}$/;
function normalizeSiren(raw) {
  const digits = raw.replace(/\s/g, "");
  return /^\d{14}$/.test(digits) ? digits.slice(0, 9) : digits;
}
function validateCompany(draft) {
  if (draft.name.trim() === "") {
    return err({ type: "invalid_name" });
  }
  if (draft.siren !== null && !SIREN_PATTERN.test(draft.siren)) {
    return err({ type: "invalid_siren" });
  }
  return ok(void 0);
}
function companyStatus(accountStatuses) {
  if (accountStatuses.length === 0) {
    return "prospect";
  }
  return accountStatuses.some((status) => status !== "disabled") ? "client" : "former_client";
}

// src/crm/company-financials.ts
var FINANCIALS_SOURCES = ["inpi", "recherche_entreprises"];
var FINANCIALS_TTL_DAYS = 30;
function latestRevenue(candidates) {
  let best = null;
  for (const candidate of candidates) {
    if (best === null || isMoreRecent(candidate, best)) {
      best = candidate;
    }
  }
  return best;
}
function isMoreRecent(a, b) {
  if (a.fiscalYearEnd !== null && b.fiscalYearEnd !== null) {
    return a.fiscalYearEnd > b.fiscalYearEnd;
  }
  if (a.fiscalYear !== b.fiscalYear) {
    return a.fiscalYear > b.fiscalYear;
  }
  return a.fiscalYearEnd !== null && b.fiscalYearEnd === null;
}
function latestFiling(filings) {
  let best = null;
  for (const filing of filings) {
    if (best === null || filing.fiscalYearEnd > best.fiscalYearEnd || filing.fiscalYearEnd === best.fiscalYearEnd && filing.confidential && !best.confidential) {
      best = filing;
    }
  }
  return best;
}
function financialsStale(financials, siren, now) {
  if (financials.siren !== siren) {
    return true;
  }
  const ageMs = now.getTime() - financials.checkedAt.getTime();
  return ageMs >= FINANCIALS_TTL_DAYS * 24 * 60 * 60 * 1e3;
}

// src/crm/contact.ts
var CONTACT_ROLES = ["decider", "user", "billing", "tech"];
var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function validateContact(draft) {
  if (draft.firstName.trim() === "" || draft.lastName.trim() === "") {
    return err({ type: "invalid_contact_name" });
  }
  if (draft.email !== null && !EMAIL_PATTERN.test(draft.email)) {
    return err({ type: "invalid_email" });
  }
  return ok(void 0);
}
function contactFullName(contact) {
  return `${contact.firstName} ${contact.lastName}`;
}
function billingContact(contacts, primaryContactId) {
  return contacts.find((c) => c.roles.includes("billing") && c.email !== null) ?? contacts.find((c) => c.id === primaryContactId && c.email !== null);
}

// src/crm/deal.ts
import Decimal7 from "decimal.js";
var OPEN_STAGES = [
  { stage: "to_qualify", probability: 10 },
  { stage: "r1_planned", probability: 20 },
  { stage: "r1_done", probability: 30 },
  { stage: "proposal_presented", probability: 50 },
  { stage: "specifications", probability: 70 },
  { stage: "signature_pending", probability: 90 }
];
var LOST_REASONS = [
  "price",
  "timing",
  "competitor",
  "no_need",
  "no_response",
  "other"
];
function defaultProbability(stage) {
  return OPEN_STAGES.find((s) => s.stage === stage)?.probability ?? 0;
}
function isStageAllowed(model, stage) {
  switch (model) {
    case "custom":
      return true;
    case "shared_license":
      return stage !== "specifications";
    default:
      return assertNever(model);
  }
}
function stagesFor(model) {
  return OPEN_STAGES.map((s) => s.stage).filter((stage) => isStageAllowed(model, stage));
}
function validateDeal(draft) {
  if (draft.title.trim() === "") {
    return err({ type: "invalid_title" });
  }
  if (!Number.isInteger(draft.probability) || draft.probability < 0 || draft.probability > 100) {
    return err({ type: "invalid_probability" });
  }
  if (draft.setupMicroEur !== null && draft.setupMicroEur < 0 || draft.monthlyMicroEur !== null && draft.monthlyMicroEur < 0) {
    return err({ type: "invalid_amount" });
  }
  if (draft.expectedCloseDate !== null && !isValidCalendarDate(draft.expectedCloseDate)) {
    return err({ type: "invalid_close_date" });
  }
  if (!isStageAllowed(draft.model, draft.stage)) {
    return err({ type: "stage_not_allowed", stage: draft.stage, model: draft.model });
  }
  return ok(void 0);
}
function dealStage(deal) {
  switch (deal.state.status) {
    case "open":
      return deal.state.stage;
    case "won":
      return "won";
    case "lost":
      return "lost";
    default:
      return assertNever(deal.state);
  }
}
function isDealPriced(deal) {
  return deal.setupMicroEur !== null || deal.monthlyMicroEur !== null;
}
function twelveMonthValue(deal) {
  if (!isDealPriced(deal)) {
    return ok(null);
  }
  const setup = deal.setupMicroEur === null ? new Decimal7(0) : microEurToDecimal(deal.setupMicroEur);
  const monthly = deal.monthlyMicroEur === null ? new Decimal7(0) : microEurToDecimal(deal.monthlyMicroEur);
  return euroDecimalToMicroEur(setup.plus(monthly.times(12)));
}
function weightedValue(deal) {
  const value = twelveMonthValue(deal);
  if (!value.ok || value.value === null) {
    return value;
  }
  return euroDecimalToMicroEur(
    microEurToDecimal(value.value).times(deal.probability).dividedBy(100)
  );
}
function autoReminderFor(stage) {
  switch (stage) {
    case "proposal_presented":
      return { subject: "Relancer apr\xE8s pr\xE9sentation", afterDays: 5, time: "09:00" };
    case "to_qualify":
    case "r1_planned":
    case "r1_done":
    case "specifications":
    case "signature_pending":
      return null;
    default:
      return assertNever(stage);
  }
}
function salesRates(closed) {
  const afterR1 = closed.filter((d) => d.reachedDiscovery);
  const wonAfterR1 = afterR1.filter((d) => d.won).length;
  const won = closed.filter((d) => d.won);
  return {
    signatureRateAfterR1: afterR1.length === 0 ? null : new Decimal7(wonAfterR1).times(100).dividedBy(afterR1.length).toDecimalPlaces(0).toNumber(),
    closedAfterR1: afterR1.length,
    wonAfterR1,
    averageCycleDays: won.length === 0 ? null : new Decimal7(won.reduce((sum2, d) => sum2 + d.cycleDays, 0)).dividedBy(won.length).toDecimalPlaces(0).toNumber()
  };
}

// src/crm/activity.ts
var SYSTEM_AUTHOR = "system";
function validateActivityContent(content) {
  switch (content.kind) {
    case "note":
    case "call":
    case "meeting":
    case "email":
      return content.body.trim() === "" ? err({ type: "empty_activity" }) : ok(void 0);
    case "discovery":
      return [
        content.report.context,
        content.report.pains,
        content.report.currentTools,
        content.report.decisionMaker,
        content.report.budget,
        content.report.timeline
      ].every((v) => v.trim() === "") ? err({ type: "empty_activity" }) : ok(void 0);
    case "system":
      return ok(void 0);
    default:
      return assertNever(content);
  }
}

// src/crm/reminder.ts
var REMINDER_TYPES = ["call", "email", "meeting", "task"];
function validateReminderSubject(subject) {
  return subject.trim() === "" ? err({ type: "invalid_subject" }) : ok(void 0);
}
function reminderTiming(reminder, now) {
  const due = parisDate(reminder.dueAt);
  const today = parisDate(now);
  if (due < today) {
    return "late";
  }
  return due === today ? "today" : "upcoming";
}
function nextReminder(reminders) {
  let next;
  for (const reminder of reminders) {
    if (reminder.doneAt === null && (next === void 0 || reminder.dueAt < next.dueAt)) {
      next = reminder;
    }
  }
  return next;
}
function validateDealDocument(input) {
  if (input.label.trim() === "" || !/^https?:\/\/\S+$/.test(input.url)) {
    return err({ type: "invalid_document" });
  }
  return ok(void 0);
}

// src/crm/deal-finance.ts
var INSTALLMENT_STATUSES = ["planned", "invoiced", "paid"];
function validatePlannedInstallment(input) {
  if (input.label.trim() === "" || input.label.length > 200) {
    return err({ type: "invalid_installment_label" });
  }
  if (input.amountMicroEur <= 0) {
    return err({ type: "invalid_installment_amount" });
  }
  if (input.dueDate !== null && !isValidCalendarDate(input.dueDate)) {
    return err({ type: "invalid_installment_date" });
  }
  return ok(void 0);
}
function validateInstallmentState(state) {
  switch (state.status) {
    case "planned":
      return ok(void 0);
    case "invoiced":
      return isValidCalendarDate(state.invoicedOn) ? ok(void 0) : err({ type: "invalid_installment_date" });
    case "paid":
      return isValidCalendarDate(state.paidOn) && (state.invoicedOn === null || isValidCalendarDate(state.invoicedOn)) ? ok(void 0) : err({ type: "invalid_installment_date" });
    default:
      return assertNever(state);
  }
}
function sum(values) {
  const total = values.reduce((acc, v) => acc + v, 0);
  const result = microEur(total);
  return result.ok ? result.value : MICRO_EUR_ZERO;
}
function signatureSummary(setupMicroEur, installments) {
  const scheduled = sum(installments.map((i) => i.amountMicroEur));
  const invoiced = sum(installments.filter((i) => i.state.status !== "planned").map((i) => i.amountMicroEur));
  const paid = sum(installments.filter((i) => i.state.status === "paid").map((i) => i.amountMicroEur));
  return {
    totalMicroEur: setupMicroEur,
    scheduledMicroEur: scheduled,
    invoicedMicroEur: invoiced,
    paidMicroEur: paid,
    outstandingMicroEur: sum(
      installments.filter((i) => i.state.status === "invoiced").map((i) => i.amountMicroEur)
    ),
    unscheduledMicroEur: setupMicroEur === null ? null : setupMicroEur - scheduled
  };
}
function isInstallmentLate(installment, today) {
  return installment.state.status !== "paid" && installment.dueDate !== null && installment.dueDate < today;
}
var PROPOSAL_STATUSES = [
  "draft",
  "sent",
  "accepted",
  "declined",
  "superseded"
];
function validateProposal(draft) {
  if (draft.title.trim() === "" || draft.title.length > 200) {
    return err({ type: "invalid_proposal_title" });
  }
  for (const amount of [draft.setupMicroEur, draft.monthlyMicroEur, draft.aiMonthlyMicroEur]) {
    if (amount !== null && amount < 0) {
      return err({ type: "invalid_amount" });
    }
  }
  for (const installment of draft.installments) {
    const check = validatePlannedInstallment(installment);
    if (!check.ok) {
      return check;
    }
  }
  return ok(void 0);
}
function proposalTitle(version) {
  return `Proposition V${version}`;
}
function isProposalOpen(proposal) {
  switch (proposal.status) {
    case "draft":
    case "sent":
      return true;
    case "accepted":
    case "declined":
    case "superseded":
      return false;
    default:
      return assertNever(proposal.status);
  }
}
var DEAL_DOCUMENT_CATEGORIES = [
  "proposal",
  "specifications",
  "contract",
  "other"
];
var MAX_DEAL_DOCUMENT_BYTES = 20 * 1024 * 1024;
function sanitizeFileName(name) {
  let printable = "";
  for (let i = 0; i < name.length; i += 1) {
    const code = name.charCodeAt(i);
    if (code >= 32 && code !== 127) {
      printable += name.charAt(i);
    }
  }
  const base = printable.replace(/[\\/]/g, "_").trim();
  const cut = base.length > 150 ? base.slice(0, 150) : base;
  return cut === "" ? "document.pdf" : cut;
}

// src/team/team-member.ts
var UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var OperatorIdCodec = {
  parse(raw) {
    if (!UUID_PATTERN.test(raw)) {
      return err({ type: "operator_id_invalid", received: raw });
    }
    return ok(raw.toLowerCase());
  },
  is(raw) {
    return UUID_PATTERN.test(raw);
  }
};
var ROLES = ["admin", "employee"];
function can(role, permission) {
  switch (role) {
    case "admin":
      return true;
    case "employee":
      return permission === "crm";
    default:
      return assertNever(role);
  }
}
function isActive(member) {
  return member.disabledAt === null;
}
function memberLabel(member) {
  return member.displayName !== null && member.displayName.trim() !== "" ? member.displayName : member.email;
}
function validateTeamChange(members, actorId, target, change) {
  const roleChanges = change.role !== void 0 && change.role !== target.role;
  const disables = change.disabled === true && target.disabledAt === null;
  if (target.id === actorId && (roleChanges || disables)) {
    return err({ type: "cannot_change_self" });
  }
  if (change.displayName !== void 0 && change.displayName !== null && change.displayName.length > 100) {
    return err({ type: "invalid_display_name" });
  }
  const after = {
    ...target,
    role: change.role ?? target.role,
    disabledAt: change.disabled === void 0 ? target.disabledAt : change.disabled ? target.disabledAt ?? /* @__PURE__ */ new Date(0) : null
  };
  const activeAdmins = members.map((m) => m.id === target.id ? after : m).filter((m) => m.role === "admin" && isActive(m));
  if (activeAdmins.length === 0) {
    return err({ type: "last_admin" });
  }
  return ok(void 0);
}
var EMAIL_PATTERN2 = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function normalizeEmail(email) {
  return email.trim().toLowerCase();
}
function validateInvite(input) {
  if (!EMAIL_PATTERN2.test(normalizeEmail(input.email))) {
    return err({ type: "invalid_email" });
  }
  if (input.displayName !== null && input.displayName.length > 100) {
    return err({ type: "invalid_display_name" });
  }
  return ok(void 0);
}

// src/crm/appointment.ts
var BOOKING_URL = /^https:\/\/[a-z0-9.-]+(:\d+)?(\/[^\s?#]*)?$/i;
function validateAppointmentType(input) {
  if (input.name.trim() === "" || input.name.length > 50) {
    return err({ type: "invalid_appointment_type_name" });
  }
  if (!BOOKING_URL.test(input.bookingUrl) || input.bookingUrl.length > 500) {
    return err({ type: "invalid_booking_url" });
  }
  if (input.calEventSlug !== null && !/^[a-z0-9][a-z0-9-]{0,99}$/i.test(input.calEventSlug)) {
    return err({ type: "invalid_event_slug" });
  }
  return ok(void 0);
}
function eventSlugOf(bookingUrl) {
  if (!BOOKING_URL.test(bookingUrl)) {
    return null;
  }
  const segments = bookingUrl.replace(/^https:\/\/[^/]+/i, "").split("/").filter((s) => s !== "");
  return segments.at(-1) ?? null;
}
function bookingLink(type, context) {
  const params = [];
  if (context.attendee !== null) {
    params.push(["name", context.attendee.name]);
    if (context.attendee.email !== null) {
      params.push(["email", context.attendee.email]);
    }
  }
  params.push(["metadata[companyId]", context.companyId]);
  if (context.dealId !== null) {
    params.push(["metadata[dealId]", context.dealId]);
  }
  const query = params.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
  return `${type.bookingUrl}?${query}`;
}
function isLaterStage(current, target) {
  const index = (stage) => OPEN_STAGES.findIndex((s) => s.stage === stage);
  return index(target) > index(current);
}
function nextAppointment(appointments, now) {
  let next;
  for (const appointment of appointments) {
    if (appointment.status === "scheduled" && appointment.endAt >= now && (next === void 0 || appointment.startAt < next.startAt)) {
      next = appointment;
    }
  }
  return next;
}
export {
  ACCEPTED_MIME_TYPES,
  ADJUSTMENTS_SECTION_TITLE,
  API_KEY_PREFIXES,
  ActivityIdCodec,
  AdjustmentIdCodec,
  AlertIdCodec,
  ApiKeyIdCodec,
  AppointmentIdCodec,
  AppointmentTypeIdCodec,
  AttemptIdCodec,
  BatchIdCodec,
  COMPANY_ORIGINS,
  CONTACT_ROLES,
  COUNTRY_CALLING_CODES,
  CapChangeIdCodec,
  ChargeIdCodec,
  ClientIdCodec,
  CompanyIdCodec,
  ContactIdCodec,
  DEAL_DOCUMENT_CATEGORIES,
  DealDocumentIdCodec,
  DealIdCodec,
  DealInstallmentIdCodec,
  DealProposalIdCodec,
  ENVIRONMENTS,
  FINANCIALS_SOURCES,
  FINANCIALS_TTL_DAYS,
  FileIdCodec,
  GENERIC_SMS_SENDERS,
  ID_PREFIXES,
  INSTALLMENT_STATUSES,
  INVOICE_DESCRIPTION_MAX,
  INVOICE_NAME_MAX,
  LOST_REASONS,
  MAX_DEAL_DOCUMENT_BYTES,
  MAX_PROVIDER_UNAVAILABLE_ATTEMPTS,
  MICRO_EUR_ZERO,
  MICRO_USD_ZERO,
  ModelIdCodec,
  OPEN_STAGES,
  OperatorIdCodec,
  PROPOSAL_STATUSES,
  PROVIDERS,
  PeriodIdCodec,
  PlatformChargeIdCodec,
  PricingRuleIdCodec,
  REMINDER_TYPES,
  ROLES,
  ReminderIdCodec,
  RequestIdCodec,
  SMS_DESTINATION_NOT_ALLOWED_MESSAGE,
  SMS_EMPTY_MESSAGE,
  SMS_MAX_SEGMENTS,
  SUBSCRIPTIONS_SECTION_TITLE,
  SYSTEM_AUTHOR,
  USAGE_SECTION_TITLE,
  UseCaseIdCodec,
  UserIdCodec,
  WEBHOOK_RETRY_SCHEDULE_MINUTES,
  WebhookDeliveryIdCodec,
  addDaysToDate,
  addMicroEur,
  addMicroUsd,
  addMonthsToDate,
  all,
  andThen,
  apiKeyMarker,
  assertNever,
  attemptCostInTariffCurrency,
  attemptCostMicroEur,
  attemptCostMicroUsd,
  autoReminderFor,
  billingContact,
  billingDate,
  billingMonth,
  bookingLink,
  can,
  canTransition,
  capValueChanged,
  checkCaps,
  companyStatus,
  compareMicroEur,
  compareMicroUsd,
  composeInvoiceDocument,
  computeAdjustmentAmount,
  computeBilledAmount,
  contactFullName,
  conversionRate,
  convertMicroUsdToMicroEur,
  dealStage,
  decideRetry,
  defaultCurrencyOf,
  defaultProbability,
  err,
  estimateProviderCostMicroEur,
  estimateReservationMicroEur,
  euroDecimalToMicroEur,
  eventSlugOf,
  exceededThresholds,
  financialsStale,
  formatMicroEur,
  formatMicroEurRounded,
  frenchDateTime,
  frenchMonthLabel,
  invoiceLineTitle,
  isAcceptedMimeType,
  isActive,
  isApiKeyActive,
  isBillable,
  isBilledEnvironment,
  isBlankSms,
  isChargeActiveForMonth,
  isClientActive,
  isDealPriced,
  isDestinationAllowed,
  isEndUserUsable,
  isErr,
  isInstallmentLate,
  isLaterStage,
  isMonthInRange,
  isOk,
  isPlatformChargeActiveForMonth,
  isProposalOpen,
  isStageAllowed,
  isTerminalStatus,
  isValidCalendarDate,
  isValidMonth,
  kindSupportsBatch,
  kindSupportsFiles,
  latestFiling,
  latestRevenue,
  map,
  mapErr,
  measureSms,
  memberLabel,
  microEur,
  microEurToCents,
  microEurToDecimal,
  microUsd,
  microUsdToDecimal,
  modelDefinitionErrorMessage,
  modelSupports,
  monthBounds,
  monthlyBilledMicroEur,
  monthlyCostMicroEur,
  nextAppointment,
  nextReminder,
  nextWebhookDelayMs,
  normalizeEmail,
  normalizeSiren,
  ok,
  parisDate,
  parisDaysBetween,
  parisInstant,
  parisInstantAfterDays,
  parisTime,
  parseCallingCodes,
  parseSmsSender,
  platformChargeMonthlyCostMicroEur,
  previousMonth,
  pricingRuleKindErrorMessage,
  proposalTitle,
  recurringChargeLabel,
  reminderTiming,
  resolveDefaultPricingRule,
  resolvePricingRule,
  salesRates,
  sanitizeFileName,
  selectConversion,
  selectExchangeRate,
  selectTariff,
  shouldRefreshLastUsed,
  signatureSummary,
  smoothFrequencyToMonthly,
  smsSettingsErrorMessage,
  smsTooLongMessage,
  stagesFor,
  statusForOutcome,
  subMicroEur,
  tariffCostInEur,
  tariffModeFor,
  transition,
  twelveMonthValue,
  unitPriceLabel,
  unwrapOr,
  usdDecimalToMicroUsd,
  useCaseCategory,
  useCaseConfigErrorMessage,
  validateActivityContent,
  validateAdjustment,
  validateAppointmentType,
  validateClientInvariants,
  validateCompany,
  validateContact,
  validateDeal,
  validateDealDocument,
  validateInstallmentState,
  validateInvite,
  validateModelFeatures,
  validatePlannedInstallment,
  validatePlatformCharge,
  validatePricingRuleForKind,
  validateProposal,
  validateRecurringCharge,
  validateReminderSubject,
  validateSmsSettings,
  validateTeamChange,
  validateUseCaseConfig,
  validateUseCaseOwnership,
  validateUserCap,
  webhookEndpointFor,
  weightedValue
};
//# sourceMappingURL=index.js.map